import Alert from '@/components/alert/alert.component';
import { PrimaryButton } from '@/components/buttons';
import { Loading } from '@/components/loading';
import { cn } from '@/lib/utils';
import { IfPermitted, UserPermissions } from '@/modules/Permissions';
import { UseMutationResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { Lock } from 'lucide-react';
import { useEffect } from 'react';
import { toast } from 'react-toastify';

type EntityType = 'farm' | 'project';

type BaseProps = {
  entityId: string | undefined;
  entityType: EntityType;
  isComplete: boolean;
  setReportRefetching: (reportRefetching: boolean) => void;
  markAsCompleteMutation: UseMutationResult<AxiosResponse, Error, string, unknown>;
};

const MarkAsCompleteBase = ({ entityId, entityType, isComplete, setReportRefetching, markAsCompleteMutation }: BaseProps) => {
  const entityName = entityType === 'farm' ? 'farm' : 'project';

  const sharedDescription = `cannot be undone without administrative support. Please make sure that all data has been logged before continuing.`;
  const description =
    entityType === 'farm'
      ? `Marking this farm as complete ${sharedDescription}`
      : `Marking this project as complete will mark all it's farms as complete. This ${sharedDescription}`;

  useEffect(() => {
    if (markAsCompleteMutation.status === 'error') {
      toast.error(markAsCompleteMutation.error?.message ?? `Failed to mark ${entityName} as complete. Please try again later.`, {
        autoClose: 3000,
      });
    }
  }, [markAsCompleteMutation.error, markAsCompleteMutation.status, entityName]);

  return (
    <IfPermitted permissions={[UserPermissions.MARK_AS_COMPLETE_BUTTON]} fallback={null}>
      {(markAsCompleteMutation.status === 'idle' || markAsCompleteMutation.status === 'error') && (
        <Alert
          trigger={
            <PrimaryButton size="small" disabled={isComplete} className="h-14">
              Mark as complete
            </PrimaryButton>
          }
          title="This action is not reversible!"
          description={description}
          actionLabel="Continue"
          onActionAccept={() => {
            if (!entityId) {
              toast.error(`Could not find ${entityName} details, please refresh and try again.`, {
                autoClose: 3000,
              });
              return;
            }
            markAsCompleteMutation.mutate(entityId, {
              onSuccess: () => {
                setReportRefetching(true);
              },
            });
          }}
        />
      )}

      {markAsCompleteMutation.status === 'pending' && (
        <div className={cn('flex gap-2 text-neutral-300 justify-center')}>
          <Loading size={'small'} inline /> Marking as complete
        </div>
      )}

      {markAsCompleteMutation.status === 'success' && (
        <div className={cn('flex gap-2 px-2 items-center text-neutral-600 py-1 justify-center bg-neutral-100 rounded-md')}>
          <Lock className={cn('white')} size="18px" />
          {entityName} marked complete.
        </div>
      )}
    </IfPermitted>
  );
};

export default MarkAsCompleteBase;
